import { useEffect, useState } from 'react';
import type { FlattenSimpleInterpolation } from 'styled-components';
import { css } from 'styled-components';

import { breakpointWidths, MAX_CONTENT_WIDTH, MIN_CONTENT_WIDTH } from '../../constants';

const singleColumnGrid = [
    ['reader'],
    ['document-info'],
    ['shelf-read-more'],
    ['shelves'],
    ['explore'],
] as const;
const coverBriefGrid = [['cover-brief'], ['lead-story'], ...singleColumnGrid] as const;
const twoColumnGrid = [
    ['reader', 'see-more'],
    ['document-info', 'see-more'],
    ['shelves', 'shelves'],
    ['explore', 'explore'],
] as const;

const layouts = {
    tiny: {
        areas: coverBriefGrid,
        query: `(max-width: ${breakpointWidths.tinyMax}px)`,
        style: css`
            min-width: ${MIN_CONTENT_WIDTH}px;
        `,
    },
    small: {
        areas: singleColumnGrid,
        query: `(min-width: ${breakpointWidths.smallMin}px) and (max-width: ${breakpointWidths.smallMax}px)`,
        style: css``,
    },
    singleColumn: {
        areas: singleColumnGrid,
        query: `(min-width: ${breakpointWidths.singleColMin}px) and (max-width: ${breakpointWidths.singleColMax}px)`,
        style: css``,
    },
    twoColumn: {
        areas: twoColumnGrid,
        query: `(min-width: ${breakpointWidths.twoColMin}px) and (max-width: ${breakpointWidths.twoColMax}px)`,
        style: css`
            max-width: ${MAX_CONTENT_WIDTH}px;
        `,
    },
    wideScreen: {
        areas: twoColumnGrid,
        query: `(min-width: ${breakpointWidths.wideScreenMin}px)`,
        style: css`
            max-width: ${MAX_CONTENT_WIDTH}px;
        `,
    },
} as const;

export type GridLayout = keyof typeof layouts;
export type GridArea = Exclude<
    (typeof singleColumnGrid | typeof coverBriefGrid | typeof twoColumnGrid)[number][number],
    '.'
>;
type LayoutSettings = Readonly<{
    areas: Readonly<Readonly<(GridArea | '.')[]>[]>;
    query: string;
    style: FlattenSimpleInterpolation;
}>;

const wideReaderLayouts: Readonly<Partial<Record<GridLayout, LayoutSettings['areas']>>> = {
    twoColumn: singleColumnGrid,
    wideScreen: singleColumnGrid,
};

const layoutNames = Object.keys(layouts) as GridLayout[];

export const getLayoutStyles = ({
    wideReader,
}: {
    wideReader: boolean;
}): FlattenSimpleInterpolation =>
    (Object.entries(layouts) as [GridLayout, LayoutSettings][]).reduce<FlattenSimpleInterpolation>(
        (
            acc: FlattenSimpleInterpolation,
            [layout, { areas, query, style }]: [GridLayout, LayoutSettings],
        ) => {
            const gridAreas = (wideReader && wideReaderLayouts[layout]) || areas;
            const columnSize =
                gridAreas[0].length === 1
                    ? ''
                    : css`
                          grid-template-columns: 1fr min-content;
                      `;
            return css`
                ${acc}
                @media ${query} {
                    ${style}
                    ${columnSize}
                    grid-template-areas: '${gridAreas.map(row => row.join(' ')).join("'\n '")}';
                }
            `;
        },
        css``,
    );

type Layout = { name: GridLayout; areas: Set<GridArea> };

const getColumnWidthSetter =
    <T,>(setter: (_: T) => void, value: T) =>
    (e: MediaQueryListEvent) => {
        if (e.matches) setter(value);
    };

export const useGridLayout = ({
    viewportWidth,
    wideReader,
}: {
    viewportWidth: number;
    wideReader: boolean;
}): GridLayout => {
    const [selectedGridLayout, setSelectedGridLayout] = useState<GridLayout>(
        viewportWidth <= breakpointWidths.tinyMax
            ? 'tiny'
            : viewportWidth <= breakpointWidths.smallMax
            ? 'small'
            : wideReader
            ? 'singleColumn'
            : 'twoColumn',
    );
    useEffect(() => {
        const layoutMatchers: Array<[ReturnType<typeof getColumnWidthSetter>, MediaQueryList]> =
            layoutNames.map(layout => {
                const query = layouts[layout].query;
                const matcher = window.matchMedia(query);
                const handler = getColumnWidthSetter(setSelectedGridLayout, layout);
                handler({ matches: matcher.matches } as MediaQueryListEvent);
                if ('addEventListener' in matcher) {
                    matcher.addEventListener('change', handler);
                } else {
                    // Safari < 14 needs this
                    matcher.addListener(handler);
                }
                return [handler, matcher];
            });
        return () => {
            layoutMatchers.forEach(([handler, matcher]) => {
                if ('removeEventListener' in matcher) {
                    matcher.removeEventListener('change', handler);
                } else {
                    // Safari < 14 needs this
                    matcher.removeListener(handler);
                }
            });
        };
    }, []);
    return selectedGridLayout;
};

// Static maps of layout names to grid areas that should be rendered
const layoutAreas: Record<GridLayout, Set<GridArea>> = layoutNames.reduce((collection, layout) => {
    const areas: LayoutSettings['areas'] = layouts[layout].areas;
    const known = new Set<GridArea>();
    areas.forEach(row => row.forEach(area => area !== '.' && known.add(area)));
    return { ...collection, [layout]: known };
}, {} as Record<GridLayout, Set<GridArea>>);
const wideReaderLayoutAreas: Record<GridLayout, Set<GridArea>> = layoutNames.reduce(
    (collection, layout) => {
        const areas = wideReaderLayouts[layout];
        if (!areas) return collection;
        const known = new Set<GridArea>();
        areas.forEach(row => row.forEach(area => area !== '.' && known.add(area)));
        return { ...collection, [layout]: known };
    },
    {} as Record<GridLayout, Set<GridArea>>,
);

export const useLayout = ({
    viewportWidth,
    wideReader,
}: {
    viewportWidth: number;
    wideReader: boolean;
}): Layout => {
    const name = useGridLayout({ viewportWidth, wideReader });
    const areas = (wideReader && wideReaderLayoutAreas[name]) || layoutAreas[name];
    return { name, areas };
};
